import React from 'react';
import bemify from 'bemify-js';

import { StaticImage } from '@zapier/common-components';

import Code from '../components/Code';
import Paragraph from '../components/Paragraph';
import StepTitle from '../components/StepTitle';
import SyntaxHighlighter from '../components/SyntaxHighlighter';

const bem = bemify('polling-trigger');

const jsCodeBlock = `
const sample = require('../samples/sample_issue');

const triggerIssue = (z, bundle) => {
  const responsePromise = z.request({
    method: 'GET',
    url: \`https://api.github.com/repos/\${bundle.inputData.repo}/issues\`,
    params: {
      filter: bundle.inputData.filter,
      state: bundle.inputData.state,
      sort: 'updated',
      direction: 'desc'
    }
  });
  return responsePromise
    .then(response => JSON.parse(response.content));
};

module.exports = {
  key: 'issue',
  noun: 'Issue',

  display: {
    label: 'New Issue',
    description: 'Triggers on a new issue.'
  },

  operation: {
    inputFields: [
      {
        key: 'repo',
        label: 'Repo',
        required: true,
        dynamic: 'repo.full_name.full_name'
      },
      {
        key: 'filter',
        required: false,
        label: 'Filter',
        choices: {
          assigned: 'assigned',
          created: 'created',
          mentioned: 'mentioned',
          subscribed: 'subscribed',
          all: 'all'
        },
        helpText: 'Default is "assigned"'
      },
      {
        key: 'state',
        required: false,
        label: 'State',
        choices: {
          open: 'open',
          closed: 'closed',
          all: 'all'
        },
        helpText: 'Default is "open"'
      }
    ],
    perform: triggerIssue,

    sample: sample
  }
};`;

const PollingTrigger = () => (
  <div className={bem()}>
    <StepTitle>
      Explore the Project: Polling for New Data with a Trigger
    </StepTitle>
    <Paragraph>
      Next, let's look at <Code>/triggers/issue.js</Code>. This code lets Zaps
      poll for new GitHub issues in a specified repo and will trigger actions if
      it finds new ones. All of the details of deduplication, keeping track of
      which things have already been processed, is handled for you automatically
      by the Zapier platform.
    </Paragraph>
    <Paragraph>
      This all looks very similar to the <Code>create</Code> code. Note however
      that this is using a GET verb. This is called a polling trigger. Zapier
      will call this every few minutes for each Zap and check for new data.
      "Rest Hooks" are the other style of trigger supported by the Zapier
      platform, and they have many advantages over polling. We'll show you some
      examples of those in the documentation.
    </Paragraph>
    <SyntaxHighlighter language="javascript">{jsCodeBlock}</SyntaxHighlighter>
    <Paragraph>
      This code will create a Zap Editor form that looks like this:
    </Paragraph>
    <StaticImage
      alt="polling screenshot"
      src="https://cdn.zappy.app/70e3433ba2e6d77b8e500ca8c2cbebe8.png"
      title="polling screenshot"
    />
  </div>
);

export default PollingTrigger;
